'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { useSystemNoticeTop } from '@/components/system-notice-position';
import { cn } from '@/lib/utils';
import { RefreshCw, X } from 'lucide-react';

export function ServiceWorkerRegistration() {
  const t = useT();
  const noticeTop = useSystemNoticeTop();
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Stash the waiting SW so the Reload button can flip
  // `skipWaiting` on the *correct* worker. Without this, clicking
  // Reload after the SW finished installing in the background was a
  // plain `location.reload()` against the OLD controller — the new
  // SW would then activate post-reload, fire another
  // `controllerchange`, and the toast popped right back.
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // NEVER run the service worker in development. sw.js serves `/_next/static/*` cache-first, but
    // Turbopack's dev chunks live at STABLE urls whose contents change on every HMR rebuild — so the
    // SW hands back a stale chunk and you get "module factory is not available" (Safari is the most
    // aggressive about this). The SW is a production-only PWA feature. Also proactively unregister any
    // worker a prior dev session left controlling this page (one reload after this fully clears it).
    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister()))
        .catch(() => {});
      return;
    }

    // Snapshot at mount: `controllerchange` events arriving *without*
    // a prior controller represent the very first SW attaching to a
    // brand-new visit (no "update" semantics). Only events fired
    // while an existing controller is in place mean the user has
    // moved from one build to another.
    const hadControllerAtMount = !!navigator.serviceWorker.controller;
    let reloading = false;

    const doReload = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };

    // Hard-reload on STALE_BUILD: the SW detected its caches are
    // corrupt / out of sync with the origin and is asking us to
    // fully reset. There is no useful UI to show in this case — the
    // page may already be broken.
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'STALE_BUILD') {
        navigator.serviceWorker.ready
          .then((reg) => reg.unregister())
          .then(() => doReload())
          .catch(() => doReload());
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);

    // The Reload button posts `SKIP_WAITING` to the new SW; the new
    // SW transitions to `activating`, runs `clients.claim()`, and
    // *then* fires this event. That's our cue to reload — the next
    // page navigation will be controlled by the new SW.
    const onControllerChange = () => {
      // Ignore the very first `controllerchange` on a fresh visit
      // (no prior controller → not an "update"). On revisits where
      // a controller already exists, we still wait for the user to
      // explicitly click Reload before flipping — the listener here
      // is the *resulting* swap from that click, not an unsolicited
      // background activation.
      if (!hadControllerAtMount) return;
      if (!reloading) doReload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    const showPrompt = (worker: ServiceWorker) => {
      // During onboarding we never want to interrupt the flow. Wait
      // for it to complete (or 5 min, whichever comes first) before
      // showing the update prompt.
      try {
        if (sessionStorage.getItem('robutler_onboarding_active')) {
          const onDone = () => {
            window.removeEventListener('onboardingComplete', onDone);
            waitingWorkerRef.current = worker;
            setUpdateReady(true);
          };
          window.addEventListener('onboardingComplete', onDone);
          setTimeout(() => {
            window.removeEventListener('onboardingComplete', onDone);
            waitingWorkerRef.current = worker;
            setUpdateReady(true);
          }, 5 * 60 * 1000);
          return;
        }
      } catch {}
      waitingWorkerRef.current = worker;
      setUpdateReady(true);
    };

    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        // A SW that finished installing while this tab was
        // backgrounded sits in `registration.waiting` by the time
        // we register. Surface the prompt immediately rather than
        // waiting for a phantom `updatefound`.
        if (registration.waiting && hadControllerAtMount) {
          showPrompt(registration.waiting);
        }
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // DO NOT auto-skipWaiting. The previous version did
              // this on every install, which silently swapped the
              // controller before the user clicked Reload — any
              // subsequent activation/claim race (multi-tab, SW
              // refetch on `update()`) then fired another
              // `controllerchange` and the "New version available"
              // toast popped back forever. We now defer skipWaiting
              // to the user's explicit Reload click.
              showPrompt(newWorker);
            }
          });
        });
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });

    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const handleReload = useCallback(() => {
    const worker = waitingWorkerRef.current;
    if (worker) {
      let reloaded = false;
      const reloadOnce = () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      };

      // Safari can miss `controllerchange` for a waiting-worker
      // promotion. Listening to the waiting worker's own state
      // transition is the root signal we care about: once it reaches
      // `activated`, a reload will be served by that worker.
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') reloadOnce();
      });
      worker.postMessage({ type: 'SKIP_WAITING' });
      return;
    }
    // Defensive fallback — if we somehow lost the worker handle
    // (e.g. controllerchange fired before this state captured),
    // just hard-reload. Worst case the user sees one extra prompt
    // if there really IS still a waiting SW.
    window.location.reload();
  }, []);

  if (!updateReady || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="sw-update-pill"
      // The pill is centred and capped to the viewport width so narrow
      // mobile viewports never word-wrap the label or push the Reload/X
      // buttons off-screen. `mx-2` keeps the pill clear of the screen
      // edges; on desktop the margin is removed so it floats freely
      // under the header. In the workspace, which has no header, it sits
      // in the band of the title bar instead of across its edge and over
      // the app (components/system-notice-position.ts).
      className={cn('fixed left-1/2 -translate-x-1/2 z-[200] max-w-[calc(100vw-1rem)] mx-2 md:mx-0', noticeTop)}
    >
      <div className="flex flex-nowrap items-center gap-2 rounded-full bg-zinc-900/90 dark:bg-zinc-100/90 text-white dark:text-zinc-900 text-sm font-medium pl-4 pr-1 py-1 shadow-lg backdrop-blur-sm border border-white/10 dark:border-black/10 whitespace-nowrap">
        <RefreshCw className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline">{t('sw.newVersion')}</span>
        <span className="sm:hidden">{t('sw.updateAvailable')}</span>
        <button
          type="button"
          onClick={handleReload}
          className="ml-2 shrink-0 rounded-full bg-white/15 dark:bg-black/10 hover:bg-white/25 dark:hover:bg-black/20 px-3 py-1 text-xs font-semibold transition-colors"
        >
          {t('sw.reload')}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label={t('actions.dismiss')}
          className="shrink-0 rounded-full p-1 hover:bg-white/15 dark:hover:bg-black/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// `navigator.serviceWorker.ready` NEVER SETTLES when no service worker is
// registered — and this app deliberately registers none in development (and
// unregisters leftovers, see the effect above), so any flow that awaits
// `.ready` unguarded hangs forever there ("Enabling…" stuck after granting
// notification permission — hit on Safari against the dev host first, but
// engine-independent). Also a production safety net: Safari has been seen
// stalling registration on flaky first loads. Bound every `.ready` await.
function swReady(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  if (process.env.NODE_ENV !== 'production') return Promise.resolve(null); // no SW in dev, by design
  return Promise.race<ServiceWorkerRegistration | null>([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

// Hook for requesting push notification permission
export function usePushNotifications() {
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      return false;
    }
    if (Notification.permission === 'granted') {
      return true;
    }
    if (Notification.permission === 'denied') {
      return false;
    }
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }, []);

  const subscribeToPush = useCallback(async (): Promise<PushSubscription | null> => {
    try {
      const response = await fetch('/api/push');
      if (!response.ok) return null;

      const { publicKey } = await response.json();
      if (!publicKey) return null;

      const registration = await swReady();
      if (!registration) return null;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      const postRes = await fetch('/api/push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription.toJSON()),
      });

      if (!postRes.ok) {
        console.error('Failed to register push subscription:', postRes.status);
        return null;
      }

      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      return null;
    }
  }, []);

  const unsubscribeFromPush = useCallback(async (): Promise<boolean> => {
    try {
      const registration = await swReady();
      if (!registration) return true; // no SW → nothing to unsubscribe
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) return true;

      await subscription.unsubscribe();

      await fetch('/api/push', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });

      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push:', error);
      return false;
    }
  }, []);

  const isSubscribed = useCallback(async (): Promise<boolean> => {
    try {
      const registration = await swReady();
      if (!registration) return false;
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch {
      return false;
    }
  }, []);

  return { requestPermission, subscribeToPush, unsubscribeFromPush, isSubscribed };
}

// Helper to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
